<template>
  <div>
    <template v-if="!loading">
      <v-app-bar flat color="transparent">
        <v-toolbar-title class="breadcrumbs d-flex align-center">
          <v-icon class="text-light-blue">{{ icons.arrowLeft }}</v-icon>
          <router-link
            class="text-decoration--none"
            :to="{ name: 'settings.trades' }"
            >Back to All Trades</router-link
          >
        </v-toolbar-title>
      </v-app-bar>

      <div v-if="!loading" class="mx-4" id="company-list">
        <div class="headline font-weight-bold my-4 text-capitalize">
          Edit Trade - {{ trade.name }}
        </div>

        <!-- Trade Details -->
        <v-row>
          <v-col md="4" sm="12" xs="12">
            <p class="mb-4 title font-weight-bold">Basic Details</p>
            <v-form ref="form" @submit.prevent="onUpdateTrade">
              <v-text-field
                outlined
                flat
                large
                label="Trade Name"
                v-model="form.name"
                :error-messages="form.$getError('name')"
              />

              <v-btn
                depressed
                large
                :loading="form.$busy"
                class="text-capitalize"
                color="primary"
                type="submit"
              >
                Update
              </v-btn>
            </v-form>
          </v-col>

          <!-- Required File Templates -->
          <v-col md="8" sm="12" xs="12">
            <p class="mb-4 title font-weight-bold">Required File Templates</p>

            <v-row>
              <v-col class="py-0">
                <!-- File Template Options -->
                <v-select
                  v-model="selectedTemplateOptions"
                  label="File Templates"
                  :items="templateOptions"
                  item-text="label"
                  item-value="id"
                  outlined
                  multiple
                ></v-select>
              </v-col>

              <v-col class="py-0">
                <!-- Add File Template Button -->
                <v-btn
                  depressed
                  x-large
                  :loading="loadingTemplateOptions"
                  class="text-capitalize"
                  color="primary"
                  type="submit"
                  @click="onAddRequiredFiles"
                >
                  Add Templates
                </v-btn>
              </v-col>
            </v-row>

            <!-- Empty Message -->
            <empty-state
              class="text-center"
              text="No Required File found."
              v-if="!hasRequiredFiles"
            />

            <!-- Required Files Table -->
            <v-simple-table v-else>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Description</th>
                  <th class="text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="requiredFile in requiredFiles"
                  :key="requiredFile.id"
                >
                  <td>{{ requiredFile.label }}</td>
                  <td>{{ requiredFile.description }}</td>
                  <td class="text-center">
                    <!-- Remove Required File -->
                    <v-btn
                      text
                      color="primary"
                      title="Remove Required File"
                      @click="removingRequiredFile(true, requiredFile)"
                    >
                      <v-icon color="red">{{ icons.mdiTrashCan }}</v-icon>
                    </v-btn>
                  </td>
                </tr>
              </tbody>
            </v-simple-table>
          </v-col>
        </v-row>
      </div>
    </template>

    <remove-template-dialog
      v-if="trade"
      v-model="isRemovingTemplate"
      :trade="trade"
      :template="template"
      @onTemplateRemoved="onTemplateRemoved"
    />

    <!-- Snackbar -->
    <v-snackbar v-model="snackbar.show" right :color="snackbar.color">
      {{ snackbar.message }}
      <template v-slot:action="{ attrs }">
        <v-btn color="white" text v-bind="attrs" @click="snackbar.show = false"
          >OK</v-btn
        >
      </template>
    </v-snackbar>

    <!-- Page Loading -->
    <loader v-model="loading" />
  </div>
</template>

<script>
import { mdiChevronLeft, mdiTrashCan } from '@mdi/js'
import { mapActions, mapGetters } from 'vuex'
import Loader from '@/components/shared/Loader'
import Form from '@/utils/form'
import { pick } from 'lodash'
import EmptyState from '@/components/shared/EmptyState'
import RemoveTemplateDialog from './components/RemoveTemplateDialog.vue'
import eventBus from '@/services/eventBus'

export default {
  components: {
    Loader,
    EmptyState,
    RemoveTemplateDialog,
  },

  data() {
    return {
      name: 'Trades',

      icons: {
        arrowLeft: mdiChevronLeft,
        mdiTrashCan,
      },

      loading: false,
      loadingTemplateOptions: false,

      snackbar: {
        show: false,
        message: null,
        color: '',
      },

      tradeId: this.$route.params.id,

      form: new Form({
        name: '',
      }),

      selectedTemplateOptions: [],
      requiredFiles: {},
      isRemovingTemplate: false,
      template: {},
    }
  },

  computed: {
    ...mapGetters('trade', ['trade']),
    ...mapGetters('template', ['templateOptions']),

    hasRequiredFiles() {
      return this.requiredFiles.length > 0
    },
  },

  created() {
    this.fetchTrade(this.tradeId)
    this.fetchTemplateOptions()
  },

  mounted() {
    eventBus.$on('snackbar', ({ message, color }) => {
      this.showSnackbar(message, color)
    })
  },

  beforeDestroy() {
    eventBus.$off(['snackbar'])
  },

  methods: {
    ...mapActions({
      getTrade: 'trade/getTrade',
      updateTrade: 'trade/updateTrade',
      getTemplateOptions: 'template/getTemplateOptions',
      addTemplate: 'trade/addTemplate',
      removeTemplate: 'trade/removeTemplate',
    }),

    async fetchTrade(id) {
      this.loading = true

      await this.getTrade(id)
        .then((res) => {
          this.form = new Form(pick(this.trade, ['name']))
          this.requiredFiles = this.trade.required_file_templates
        })
        .catch((err) => {
          console.log(err.data.message)
        })
        .finally(() => {
          this.loading = false
        })
    },

    async onUpdateTrade() {
      this.form.$busy = true

      await this.updateTrade({ id: this.tradeId, trade: this.form.$data() })
        .then((res) => {
          this.form.$setErrors({})
          this.showSnackbar('Trade updated!', 'success')
        })
        .catch((err) => {
          this.form.$setErrors(err.data.errors)
          this.showSnackbar(err.data.message, 'red')
        })
        .finally(() => {
          this.form.$busy = false
        })
    },

    async fetchTemplateOptions() {
      this.loadingTemplateOptions = true

      await this.getTemplateOptions()
        .catch((err) => {
          this.showSnackbar(err.data.message, 'red')
        })
        .finally(() => {
          this.loadingTemplateOptions = false
        })
    },

    async onAddRequiredFiles() {
      this.loadingTemplateOptions = true

      await this.addTemplate({
        id: this.tradeId,
        templateIds: this.selectedTemplateOptions,
      })
        .then((res) => {
          this.requiredFiles = this.trade.required_file_templates
          this.selectedTemplateOptions = []
          this.showSnackbar('Required Files added successfully!', 'success')
        })
        .catch((err) => {
          this.showSnackbar(err.data.message, 'red')
        })
        .finally(() => {
          this.loadingTemplateOptions = false
        })
    },

    removingRequiredFile(status, requiredFile) {
      this.template = requiredFile
      this.isRemovingTemplate = status
    },

    onTemplateRemoved() {
      this.removingRequiredFile(false, {})
      this.requiredFiles = this.trade.required_file_templates
    },

    showSnackbar(message, color) {
      this.snackbar.message = message
      this.snackbar.show = true
      this.snackbar.color = color
    },
  },
}
</script>

<style>
input[readonly] {
  cursor: pointer !important;
}
</style>
